import {
  SkyButton,
  SkyCard,
  SkyDataGrid,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { ScopedRulesView } from "@shared/types/notifications";
import { LoadingRows } from "@/components/access/ScreenStates";
import { ErrorState } from "@/components/platform/ScreenState";
import { ScopedRuleDrawer } from "@/components/settings/ScopedRuleDrawer";
import { useScopedRules } from "@/hooks/use-alert-routing";
import { AddIcon, EditIcon } from "@/lib/icons";
import { appliesToText, recipientsText, whenText } from "@/lib/notifications/scopedRules";
import { OPEN_RECIPIENT_POLICY, type RecipientPolicy } from "@shared/types/notificationDomains";

/**
 * Settings > Notifications, below the nine rules and above Recent deliveries (alert routing,
 * docs/research/2026-09-28-design-alert-routing.md, "UI"): the rules for chosen connections or
 * schedules. Each one ADDS recipients for the connections or schedules it names; none of them
 * ever stops the rules above (owner, 2026-09-28), and the card says so under its title, because
 * "additive" is the one thing people will assume wrongly.
 *
 * A writer (`instance.settings.write`) adds or edits a rule in a drawer, which keeps the list in
 * view; a reader sees the same list with masked addresses and nothing to press.
 */

type Row = ScopedRulesView["rules"][number];

/** The grid's columns; the Actions column only for a writer. */
function scopedRuleColumns(
  canWrite: boolean,
  onEdit: (row: Row) => void,
  policy: RecipientPolicy,
): SkyDataGridColumn<Row>[] {
  return [
    {
      key: "name",
      header: "Name",
      width: "20%",
      renderCell: (_value, row) => <SkyText weight="semibold">{row.name}</SkyText>,
    },
    {
      key: "targets",
      header: "Applies to",
      width: "28%",
      renderCell: (_value, row) => <SkyText size={200}>{appliesToText(row)}</SkyText>,
    },
    {
      key: "outcomes",
      header: "When",
      width: "16%",
      renderCell: (_value, row) => <SkyText size={200}>{whenText(row.outcomes)}</SkyText>,
    },
    {
      key: "emails",
      header: "Recipients",
      width: "22%",
      renderCell: (_value, row) => (
        <SkyText size={200} className="break-all">
          {recipientsText(row, policy)}
        </SkyText>
      ),
    },
    {
      key: "enabled",
      header: "On",
      width: canWrite ? "6%" : "14%",
      renderCell: (_value, row) => <SkyText size={200}>{row.enabled ? "On" : "Off"}</SkyText>,
    },
    ...(canWrite
      ? [
          {
            key: "actions",
            header: "Actions",
            width: "8%",
            align: "right" as const,
            renderCell: (_value: unknown, row: Row) => (
              <SkyButton
                appearance="subtle"
                size="small"
                icon={EditIcon}
                aria-label={`Edit ${row.name}`}
                testId={`scoped-rule-edit-${row.id}`}
                onClick={() => onEdit(row)}
              />
            ),
          },
        ]
      : []),
  ];
}

/** Loading, error, empty, or the grid. */
function ScopedRulesBody({
  rules,
  columns,
  hasRules,
}: {
  rules: ReturnType<typeof useScopedRules>;
  columns: SkyDataGridColumn<Row>[];
  hasRules: boolean;
}) {
  const view = rules.data;
  if (rules.isPending) return <LoadingRows rows={2} columns={5} />;
  if (rules.isError) {
    return (
      <ErrorState
        title="Rules for chosen connections did not load"
        error={rules.error}
        onRetry={() => void rules.refetch()}
        testId="scoped-rules-error"
      />
    );
  }
  if (view && view.rules.length === 0) {
    return (
      <SkyText as="p" size={300} block testId="scoped-rules-empty">
        {hasRules
          ? "No rule for chosen connections yet. The rules above apply to every connection."
          : "No rule for chosen connections yet."}
      </SkyText>
    );
  }
  if (!view) return null;
  return (
    <SkyDataGrid<Row>
      data={view.rules}
      columns={columns}
      getRowId={(row) => row.id}
      ariaLabel="Rules for chosen connections and schedules"
      testId="scoped-rules-grid"
    />
  );
}

export function ScopedRulesCard({ canWrite, hasRules }: { canWrite: boolean; hasRules: boolean }) {
  const rules = useScopedRules();
  // `null`: closed; `"new"`: adding; otherwise the rule being edited.
  const [editing, setEditing] = useState<Row | "new" | null>(null);
  const view = rules.data;

  const columns = scopedRuleColumns(
    canWrite,
    setEditing,
    view?.recipientPolicy ?? OPEN_RECIPIENT_POLICY,
  );

  return (
    <SkyCard testId="scoped-rules-card" className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex flex-col gap-1">
          <SkyText as="h3" size={300} weight="semibold" block>
            Rules for chosen connections and schedules
          </SkyText>
          <SkyText as="p" size={200} color="muted" block>
            Rules for chosen connections add recipients. They never stop the rules above.
          </SkyText>
        </div>
        {canWrite ? (
          <SkyButton
            appearance="secondary"
            icon={AddIcon}
            className="ml-auto"
            testId="scoped-rules-add"
            disabled={!view}
            onClick={() => setEditing("new")}
          >
            Add a rule for chosen connections
          </SkyButton>
        ) : null}
      </div>

      <ScopedRulesBody rules={rules} columns={columns} hasRules={hasRules} />

      {editing && view ? (
        <ScopedRuleDrawer
          view={view}
          rule={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </SkyCard>
  );
}
